import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Phone, ArrowLeft, Mail } from 'lucide-react';
import { setTokenPair, getRememberMe, type TokenPair } from '../lib/auth';
import { apiPost } from '../lib/api';
import { AuthLayout } from '../components/layout/AuthLayout';
import { useAuth } from '../contexts/AuthContext';
import { useAuthConfig } from '../hooks/useAuthConfig';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean(),
});

type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();
  const { data: config, isLoading: configLoading } = useAuthConfig();
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone' | 'magic-link'>('email');

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { rememberMe: getRememberMe() },
  });

  const onSubmit = async (data: FormData) => {
    setApiError('');
    try {
      const res = await apiPost<TokenPair>('/customer/auth/login', {
        email: data.email,
        password: data.password,
      }, { skipAuth: true });
      setTokenPair(res, data.rememberMe);
      refreshAuth();
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      setApiError(status === 401 ? 'Invalid email or password.' : 'Something went wrong. Please try again.');
    }
  };

  const handleSocialLogin = (provider: 'google' | 'meta') => {
    window.location.href = `/customer/auth/${provider}?redirect_uri=${encodeURIComponent(window.location.origin + from)}`;
  };

  // Show loading skeleton while config loads
  if (configLoading) {
    return (
      <AuthLayout title="Sign in" subtitle="Welcome back">
        <div className="space-y-5 animate-pulse">
          <div className="h-10 bg-zinc-100 rounded-lg" />
          <div className="h-10 bg-zinc-100 rounded-lg" />
          <div className="h-10 bg-zinc-200 rounded-lg" />
        </div>
      </AuthLayout>
    );
  }

  // Derive which methods are enabled
  const emailEnabled = config?.methods.emailPassword.enabled ?? true;
  const googleEnabled = config?.methods.google.enabled ?? true;
  const metaEnabled = config?.methods.meta.enabled ?? true;
  const phoneEnabled = config?.methods.phoneOtp.enabled ?? true;
  const magicLinkEnabled = config?.methods.magicLink.enabled ?? false;
  const signupEnabled = config?.signup.enabled ?? true;

  const hasSocial = googleEnabled || metaEnabled;
  const hasAlternate = phoneEnabled || magicLinkEnabled;

  if (loginMethod === 'phone') {
    return (
      <AuthLayout title="Sign in" subtitle="Enter your phone number">
        <PhoneLoginFlow
          onBack={() => setLoginMethod('email')}
          onSuccess={(tokens) => {
            setTokenPair(tokens, getRememberMe());
            refreshAuth();
            navigate(from, { replace: true });
          }}
        />
      </AuthLayout>
    );
  }

  if (loginMethod === 'magic-link') {
    return (
      <AuthLayout title="Sign in" subtitle="We'll email you a sign-in link">
        <MagicLinkFlow onBack={() => setLoginMethod('email')} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        {/* Email + Password form */}
        {emailEnabled && (
          <>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="input-field"
                placeholder="you@example.com"
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-zinc-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs font-medium text-[--color-primary] hover:opacity-80">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="input-field pr-10"
                  placeholder="••••••••"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
            </div>

            <div className="flex items-center gap-2.5">
              <input
                id="rememberMe"
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-300 text-[--color-primary] focus:ring-[--color-primary] cursor-pointer"
                {...register('rememberMe')}
              />
              <label htmlFor="rememberMe" className="text-sm text-zinc-600 cursor-pointer select-none">
                Remember me
              </label>
            </div>

            {apiError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                <p className="text-sm text-red-600">{apiError}</p>
              </div>
            )}

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : 'Sign in'}
            </button>
          </>
        )}

        {/* Social divider */}
        {emailEnabled && hasSocial && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-zinc-400">or continue with</span>
            </div>
          </div>
        )}

        {/* Social buttons */}
        {hasSocial && (
          <div className={`grid gap-3 ${googleEnabled && metaEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {googleEnabled && (
              <button type="button" onClick={() => handleSocialLogin('google')} className="social-btn">
                <GoogleIcon /> Google
              </button>
            )}
            {metaEnabled && (
              <button type="button" onClick={() => handleSocialLogin('meta')} className="social-btn">
                <MetaIcon /> Meta
              </button>
            )}
          </div>
        )}

        {/* Alternate methods divider */}
        {(emailEnabled || hasSocial) && hasAlternate && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-zinc-400">
                {phoneEnabled && magicLinkEnabled ? 'or use' : phoneEnabled ? 'or use your phone' : 'or use email link'}
              </span>
            </div>
          </div>
        )}

        {/* Phone OTP button */}
        {phoneEnabled && (
          <button
            type="button"
            onClick={() => setLoginMethod('phone')}
            className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-[--color-primary] bg-white px-4 py-2.5 text-sm font-semibold text-[--color-primary] hover:bg-[--color-primary] hover:text-white transition-colors"
          >
            <Phone size={16} />
            Sign in with phone number (OTP)
          </button>
        )}

        {/* Magic link button */}
        {magicLinkEnabled && (
          <button
            type="button"
            onClick={() => setLoginMethod('magic-link')}
            className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-[--color-primary] bg-white px-4 py-2.5 text-sm font-semibold text-[--color-primary] hover:bg-[--color-primary] hover:text-white transition-colors"
          >
            <Mail size={16} />
            Sign in with magic link
          </button>
        )}

        {/* Sign up link */}
        {signupEnabled && (
          <p className="text-center text-sm text-zinc-500">
            Don't have an account?{' '}
            <Link to="/signup" className="font-semibold text-[--color-primary] hover:opacity-80">
              Create one
            </Link>
          </p>
        )}
      </form>
    </AuthLayout>
  );
}

// ─── Magic Link Flow ─────────────────────────────────────────────────────────

function MagicLinkFlow({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Enter your email address.'); return; }
    setLoading(true);
    try {
      await apiPost('/customer/auth/magic-link/send', { email: email.trim() }, { skipAuth: true });
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-5 text-center">
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-6">
          <Mail size={24} className="mx-auto mb-2 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-800">Check your email for a sign-in link</p>
          <p className="mt-1 text-xs text-emerald-600">We sent a link to {email}</p>
        </div>
        <button
          type="button"
          onClick={() => { setSent(false); setEmail(''); }}
          className="text-sm text-[--color-primary] hover:opacity-80 font-medium"
        >
          Resend link
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full flex items-center justify-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
        >
          <ArrowLeft size={14} /> Back to email sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSend} className="space-y-5" noValidate>
      <div>
        <label htmlFor="magic-email" className="block text-sm font-medium text-zinc-700 mb-1.5">
          Email address
        </label>
        <input
          id="magic-email"
          type="email"
          autoComplete="email"
          className="input-field"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      <button type="submit" disabled={loading} className="btn-primary w-full">
        {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : 'Send magic link'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="w-full flex items-center justify-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
      >
        <ArrowLeft size={14} /> Back to email sign in
      </button>
    </form>
  );
}

// ─── Phone Login Flow ─────────────────────────────────────────────────────────

function PhoneLoginFlow({
  onBack,
  onSuccess,
}: {
  onBack: () => void;
  onSuccess: (tokens: TokenPair) => void;
}) {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!phone.trim()) { setError('Enter your phone number.'); return; }
    setLoading(true);
    try {
      await apiPost('/customer/auth/phone/send-otp', { phone: phone.trim() }, { skipAuth: true });
      setInfo('If a verified account exists for that number, a code has been sent.');
      setStep('otp');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setLoading(true);
    try {
      const res = await apiPost<TokenPair>('/customer/auth/phone/verify-otp', {
        phone: phone.trim(),
        code: otp.trim(),
      }, { skipAuth: true });
      onSuccess(res);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      setError(status === 401 ? 'Invalid or expired code.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {step === 'phone' ? (
        <form onSubmit={handleSendCode} className="space-y-5" noValidate>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-zinc-700 mb-1.5">
              Phone number
            </label>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              className="input-field"
              placeholder="+1 555 000 0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3"><p className="text-sm text-red-600">{error}</p></div>}
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : 'Send code'}
          </button>
          <button type="button" onClick={onBack} className="w-full flex items-center justify-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors">
            <ArrowLeft size={14} /> Back to email sign in
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerify} className="space-y-5" noValidate>
          {info && <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3"><p className="text-sm text-blue-700">{info}</p></div>}
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-zinc-700 mb-1.5">Verification code</label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="input-field tracking-widest text-center text-xl font-mono"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <p className="mt-1.5 text-xs text-zinc-400">Sent to {phone}. Check your messages.</p>
          </div>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3"><p className="text-sm text-red-600">{error}</p></div>}
          <button type="submit" disabled={loading || otp.length !== 6} className="btn-primary w-full">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : 'Sign in'}
          </button>
          <div className="flex flex-col gap-2 text-center">
            <button type="button" onClick={() => { setStep('phone'); setOtp(''); setError(''); setInfo(''); }} className="text-sm text-[--color-primary] hover:opacity-80 font-medium">
              Resend code
            </button>
            <button type="button" onClick={onBack} className="flex items-center justify-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors">
              <ArrowLeft size={14} /> Back to email sign in
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Social icons ─────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function MetaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}
